/*
 * AI search client — Reformation adapter (neurogan.com).
 * Reads window.AISearch (snippets/ai-search-config.liquid). Exposes
 * window.AISearchClient with fetch helpers + Reformation-markup renderers.
 * Fail-soft: query() resolves null on any non-OK/error so callers can fall
 * back to native search untouched.
 */
(function () {
  var cfg = window.AISearch || {};

  function ready() {
    return !!(cfg.enabled && cfg.url && cfg.tenant);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Matches the store's money format: whole amounts without decimals ($400).
  // Locale pinned to en-US — the store renders "$90" for every shopper; an
  // unpinned Intl locale formatted it as "90 US$" on non-US browsers.
  function formatPrice(price) {
    if (price == null) return '';
    var n = Number(price);
    var whole = n % 1 === 0;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: cfg.currency || 'USD',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(n);
    } catch (e) {
      return '$' + (whole ? n.toFixed(0) : n.toFixed(2));
    }
  }

  // Resolve cross-store links + apply merchant pins (assets/ai-search-shape.js).
  function shape(data) {
    var S = window.AISearchShape;
    return S ? S.shapeResult(data, cfg.pins) : data;
  }

  function query(term, opts) {
    opts = opts || {};
    if (!ready()) return Promise.resolve(null);
    var t = (term || '').trim();
    if (t.length < 2) return Promise.resolve({ query: t, products: [], articles: [], bestMatch: null });
    var base = cfg.url.replace(/\/+$/, '');
    var url = base + '/api/search?tenant=' + encodeURIComponent(cfg.tenant) + '&q=' + encodeURIComponent(t);
    return fetch(url, { signal: opts.signal })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(shape)
      .catch(function (e) {
        if (e && e.name === 'AbortError') throw e;
        return null;
      });
  }

  function trending(limit) {
    if (!ready()) return Promise.resolve([]);
    var base = cfg.url.replace(/\/+$/, '');
    var url = base + '/api/trending?tenant=' + encodeURIComponent(cfg.tenant) + '&limit=' + (limit || 8);
    return fetch(url)
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        return ((data && data.queries) || []).map(function (q) { return q.query; });
      })
      .catch(function () { return []; });
  }

  function reviewsHighlights(limit, handles, q) {
    if (!ready()) return Promise.resolve([]);
    var base = cfg.url.replace(/\/+$/, '');
    var url = base + '/api/reviews-highlights?tenant=' + encodeURIComponent(cfg.tenant) + '&limit=' + (limit || 4);
    if (handles && handles.length) url += '&products=' + encodeURIComponent(handles.slice(0, 10).join(','));
    if (q) url += '&q=' + encodeURIComponent(q);
    return fetch(url)
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) { return (data && data.reviews) || []; })
      .catch(function () { return []; });
  }

  // Star rating — reuses the theme's rating.css mechanism (`.star-rating`
  // with the --star-rating var); component-ai-search.css carries a fallback
  // copy of those rules for surfaces where rating.css isn't loaded.
  function renderStars(rating, count) {
    if (!rating || !count) return '';
    return '<div class="product-card-rating-left ai-card-rating" aria-label="' +
      escapeHtml(String(rating)) + ' stars out of 5 based on ' + count + ' reviews">' +
      '<span>' + escapeHtml(String(Math.round(Number(rating) * 10) / 10)) + '</span>' +
      '<div class="star-rating" style="--star-rating: ' + escapeHtml(String(rating)) + ';"></div>' +
      '<span>(' + count + ')</span></div>';
  }

  /*
   * One result card for the /search + collection grids, in Reformation's own
   * product-card vocabulary. <product-card> is the theme's custom element —
   * injected markup upgrades on insertion and inherits theme behavior. The CTA
   * is a working add-to-cart (per spec) instead of the native "+ → PDP" link;
   * "Choose options" routes multi-variant products to the PDP.
   */
  function renderCard(item, surface, position) {
    var isProduct = item.kind === 'product';
    var attrs = ' data-ai-result data-ai-item="' + escapeHtml(item.slug) + '" data-ai-kind="' + (isProduct ? 'product' : 'article') + '"' +
      ' data-ai-position="' + (position || 0) + '" data-ai-surface="' + escapeHtml(surface || '') + '"';
    if (!isProduct) {
      var snip = item.snippet ? '<p class="ai-article-card__snippet">' + escapeHtml(item.snippet) + '</p>' : '';
      return '<li class="column"><a class="ai-article-card" href="' + escapeHtml(item.url) + '"' + attrs + '>' +
        '<span class="ai-article-card__badge">Article</span>' +
        '<h3 class="ai-article-card__title">' + escapeHtml(item.title) + '</h3>' + snip + '</a></li>';
    }
    var img = item.image
      ? '<img class="product-primary-image" src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" loading="lazy">'
      : '';
    var price = item.price != null
      ? '<span class="amount product-card-onetime-price">' + (item.hasOptions ? 'From ' : '') + escapeHtml(formatPrice(item.price)) + '</span>'
      : '';
    var button;
    if (item.available === false) {
      button = '<button class="button ai-card-btn" disabled><span>Sold out</span></button>';
    } else if (item.hasOptions || !item.variantId) {
      button = '<a class="button ai-card-btn" href="' + escapeHtml(item.url) + '"' + attrs + '><span>Choose options</span></a>';
    } else {
      button = '<button class="button ai-card-btn" data-ai-atc="' + escapeHtml(item.variantId) + '"' +
        ' data-ai-handle="' + escapeHtml(item.slug) + '" data-ai-btn-position="' + (position || 0) + '"' +
        ' data-ai-btn-surface="' + escapeHtml(surface || '') + '"><span>Add to cart</span></button>';
    }
    return (
      '<li class="column">' +
      '<product-card class="product-card text-left ai-search-card">' +
      '<a href="' + escapeHtml(item.url) + '" title="' + escapeHtml(item.title) + '" class="product-card_info"' + attrs + '>' +
      '<div class="product-featured-image__wrapper"><figure class="product-featured-image">' +
      '<div class="product-featured-image-link aspect-ratio aspect-ratio--square" style="--padding-bottom: 100%;">' + img + '</div>' +
      '</figure></div>' +
      '<div class="product-cart-info__wrapper"><div class="product-card-info">' +
      renderStars(item.rating, item.ratingCount) +
      '<div class="product-card-title product-card-title-large">' + escapeHtml(item.title) + '</div>' +
      '</div></div></a>' +
      '<div class="product-card_button product-card-button-container"><div class="product-card-price-cta">' +
      '<div class="product-card-prices">' + price + '</div>' + button +
      '</div></div>' +
      '</product-card></li>'
    );
  }

  /*
   * NATIVE card rendering: fetch the theme's REAL product-card snippet per
   * result via the card-only alternate template (templates/product.card.liquid,
   * /products/<handle>?view=card) — metafield ratings, tag pills, compare-at +
   * subscription pricing, swatches, all bit-for-bit identical to category
   * cards, upgraded by app.js on insertion.
   * The live card is the storefront truth for stock: a 404 (discontinued or
   * unpublished) or an "Out of stock" card resolves '' so every surface drops
   * it, even when the index's `available` flag is stale. Any other fetch error
   * falls back to the lean JS-rendered card (fail-soft).
   */
  var SOLD_OUT = /\bout\s+of\s+stock\b/i;
  function fetchNativeCard(item, surface, position) {
    return fetch('/products/' + encodeURIComponent(item.slug) + '?view=card')
      .then(function (r) {
        if (r.status === 404) return '';
        if (!r.ok) throw new Error('card ' + r.status);
        return r.text().then(function (html) {
          if (html.indexOf('product-card') === -1) throw new Error('not a card');
          if (SOLD_OUT.test(html)) return '';
          // data-ai attrs go on the wrapping <li>: the analytics delegate logs
          // clicks from the closest [data-ai-result], so native links inside
          // the card stay attributed without touching the card markup.
          return '<li class="column" data-ai-result data-ai-item="' + escapeHtml(item.slug) + '"' +
            ' data-ai-kind="product" data-ai-position="' + (position || 0) + '"' +
            ' data-ai-surface="' + escapeHtml(surface || '') + '">' + html + '</li>';
        });
      })
      .catch(function () { return renderCard(item, surface, position); });
  }

  // Cards that survived the stock check, in rank order.
  function renderCardList(products, surface) {
    return Promise.all(products.map(function (item, i) {
      return fetchNativeCard(item, surface || '', i + 1);
    })).then(function (cards) { return cards.filter(Boolean); });
  }

  function renderProductCards(products, surface) {
    return renderCardList(products, surface).then(function (cards) { return cards.join(''); });
  }

  // "Guides & articles" section markup (string). Products and articles never
  // mix in one grid — image-less text cards float badly next to product cards.
  function renderArticlesSection(articles, surface, startPos) {
    if (!articles || !articles.length) return '';
    return '<div class="ai-articles"><h3 class="ai-discovery__title">Guides &amp; articles</h3>' +
      '<ul class="ai-articles__list" role="list">' +
      articles.map(function (a, i) {
        var snip = a.snippet ? '<p class="ai-article-card__snippet">' + escapeHtml(a.snippet) + '</p>' : '';
        return '<li><a class="ai-article-card" href="' + escapeHtml(a.url) + '"' +
          ' data-ai-result data-ai-item="' + escapeHtml(a.slug) + '" data-ai-kind="article"' +
          ' data-ai-position="' + ((startPos || 0) + i + 1) + '" data-ai-surface="' + escapeHtml(surface || '') + '">' +
          '<h3 class="ai-article-card__title">' + escapeHtml(a.title) + '</h3>' + snip + '</a></li>';
      }).join('') + '</ul></div>';
  }

  // Keep the page's result counts honest with the AI total (facets-bar count +
  // the search banner's "N results for X" line).
  function syncCounts(total, query) {
    try {
      var label = total + (total === 1 ? ' result' : ' results') + (query ? ' for “' + query + '”' : '');
      document.querySelectorAll('#ProductCount .facets__label').forEach(function (el) { el.textContent = label; });
      document.querySelectorAll('.collection-banner p').forEach(function (el) {
        if (/result/i.test(el.textContent) && /\d/.test(el.textContent)) {
          el.textContent = el.textContent.replace(/\d[\d,]*/, String(total));
        }
      });
    } catch (e) {}
  }

  /*
   * SURGICAL grid swap. Replaces ONLY the native #product-grid's <li> items
   * (so the facets sidebar, the facets bar, and the grid's own column class —
   * medium-up-{columns_desktop} — are all preserved), drops native pagination
   * (AI returns one ranked set), appends the articles section below, and syncs
   * the counts. Resolves false when there's no native grid to patch (e.g. the
   * native search found zero results) so the caller can fall back.
   */
  function applyResults(data, surface, query) {
    var products = data.products || [];
    var articles = data.articles || [];
    var grid = document.getElementById('product-grid');
    if (!grid) return Promise.resolve(false);
    return renderCardList(products, surface).then(function (cards) {
      var total = cards.length + articles.length;
      grid.innerHTML = cards.join('');
      var wrap = grid.parentNode;
      if (wrap) {
        var pag = wrap.querySelector('pagination-theme, .pagination');
        if (pag && pag.parentNode) pag.parentNode.removeChild(pag);
        var oldArt = wrap.querySelector('.ai-articles');
        if (oldArt && oldArt.parentNode) oldArt.parentNode.removeChild(oldArt);
        var art = renderArticlesSection(articles, surface, products.length);
        if (art) grid.insertAdjacentHTML('afterend', art);
      }
      syncCounts(total, query);
      return true;
    });
  }

  /*
   * STANDALONE grid markup (string) — used only when there's no native grid to
   * patch: the native search returned zero results but AI found some (semantic
   * recall). The caller drops this into the outer container. `columns` matches
   * the section's desktop column setting (data-ai-columns), default 4.
   */
  function renderGridContainer(data, surface, query, columns) {
    var products = data.products || [];
    var articles = data.articles || [];
    var cols = columns || 4;
    return renderCardList(products, surface).then(function (list) {
      var cards = list.join('');
      var total = list.length + articles.length;
      var html = '<div class="ai-results-head"><span class="ai-results-count">' +
        total + (total === 1 ? ' result' : ' results') +
        (query ? ' for &ldquo;' + escapeHtml(query) + '&rdquo;' : '') + '</span></div>';
      if (cards) {
        html += '<ul id="product-grid" class="products collection row small-up-2 medium-up-' + cols + ' ai-search-grid" role="list">' + cards + '</ul>';
      }
      html += renderArticlesSection(articles, surface, products.length);
      return html;
    });
  }

  // Some review bodies arrive percent-encoded from import ("Our%20Dog..."); decode
  // those, leave already-plain text alone, and never throw on a stray "%".
  function decodeReviewText(s) {
    if (!s) return '';
    try { return /%[0-9A-Fa-f]{2}/.test(s) ? decodeURIComponent(s) : s; }
    catch (e) { return s; }
  }

  // Recency label — relative while fresh (so reviews read as CURRENT, not
  // back-filled), absolute month/year once older.
  function formatReviewDate(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    var days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days < 0) days = 0;
    if (days <= 1) return 'Just now';
    if (days < 7) return days + ' days ago';
    if (days < 14) return 'Last week';
    if (days < 31) return Math.round(days / 7) + ' weeks ago';
    if (days < 60) return 'Last month';
    if (days < 365) return Math.round(days / 30) + ' months ago';
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }

  // Compact drawer card (2-col grid). Link-only — the full set is one tap away.
  function renderDrawerCard(p, i) {
    var img = p.image
      ? '<img class="ai-drawer-card__img" src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="160" height="160">'
      : '<span class="ai-drawer-card__img ai-drawer-card__img--ph"></span>';
    var price = p.price != null
      ? '<span class="ai-drawer-card__price">' + (p.hasOptions ? 'From ' : '') + escapeHtml(formatPrice(p.price)) + '</span>'
      : '';
    return '<a class="ai-drawer-card" href="' + escapeHtml(p.url) + '"' +
      ' data-ai-result data-ai-item="' + escapeHtml(p.slug) + '" data-ai-kind="product"' +
      ' data-ai-position="' + i + '" data-ai-surface="header">' +
      img + '<span class="ai-drawer-card__text">' +
      '<span class="ai-drawer-card__title">' + escapeHtml(p.title) + '</span>' + price +
      '</span></a>';
  }

  /*
   * Drawer results — replaces the native tabbed panel content inside
   * .side-panel-content--has-tabs. Deliberately tab-free (stacked, products
   * first): the theme's SidePanelContentTabs couples buttons to panels BY
   * POSITION, which our reordered layout would break. The structure keeps the
   * theme's scroll-shadow + --inner + --search-footer skeleton so the drawer's
   * flex/scroll CSS applies unchanged, and keeps #search-results-submit so the
   * native rebinding in renderSearchResults keeps working.
   */
  var DRAWER_PRODUCTS = 4;
  var DRAWER_ARTICLES = 2;
  function renderDrawer(data, term) {
    var products = (data.products || []).slice(0, DRAWER_PRODUCTS);
    var articles = (data.articles || []).slice(0, DRAWER_ARTICLES);
    var suggestions = (data.suggestions || []).slice(0, DRAWER_PRODUCTS);

    var html = '<scroll-shadow><div class="side-panel-content--inner ai-drawer">';
    if (data.didYouMean) {
      html += '<div class="ai-didyoumean"><a href="/search?q=' + encodeURIComponent(data.didYouMean) + '">' +
        'Did you mean &ldquo;<strong>' + escapeHtml(data.didYouMean) + '</strong>&rdquo;?</a></div>';
    }
    if (products.length) {
      html += '<div class="ai-drawer-group"><span class="thb-predictive-search--label">Products</span>' +
        '<div class="ai-drawer-products">' +
        products.map(function (p, i) { return renderDrawerCard(p, i + 1); }).join('') + '</div></div>';
    } else if (suggestions.length) {
      html += '<div class="ai-drawer-group"><span class="thb-predictive-search--label">No exact match &mdash; shoppers often choose</span>' +
        '<div class="ai-drawer-products">' +
        suggestions.map(function (p, i) { return renderDrawerCard(p, i + 1); }).join('') + '</div></div>';
    }
    if (articles.length) {
      html += '<div class="ai-drawer-group ai-drawer-group--articles"><span class="thb-predictive-search--label">Guides &amp; articles</span>' +
        articles.map(function (a, i) {
          return '<a class="ai-drawer-article" href="' + escapeHtml(a.url) + '"' +
            ' data-ai-result data-ai-item="' + escapeHtml(a.slug) + '" data-ai-kind="article"' +
            ' data-ai-position="' + (i + 1) + '" data-ai-surface="header">' +
            escapeHtml(a.title) + '</a>';
        }).join('') + '</div>';
    }
    html += '<div class="ai-drawer-reviews-slot"></div>';
    html += '</div></scroll-shadow>';
    html += '<div class="side-panel-content--search-footer">' +
      '<button class="button full" id="search-results-submit"><span>View all results</span></button></div>';
    return html;
  }

  // Compact review cards ("Loved by shoppers"). Leads with the star rating + how
  // recent the review is, then the review's own title (strong sentiment), the
  // body, and reviewer · verified · which product.
  function renderReviews(reviews, surface, compact) {
    if (!reviews || !reviews.length) return '';
    return '<div class="ai-reviews' + (compact ? ' ai-reviews--drawer' : '') + '">' +
      '<h3 class="ai-discovery__title">Loved by shoppers' + (compact ? '' : ' right now') + '</h3>' +
      reviews.map(function (r) {
        var p = r.product || {};
        var img = p.image
          ? '<img class="ai-review-card__img" src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title || '') + '" loading="lazy">'
          : '';
        var rating = Math.max(0, Math.min(5, Math.round(Number(r.rating) || 5)));
        var stars = ''; for (var i = 0; i < 5; i++) stars += (i < rating) ? '★' : '☆';
        var body = decodeReviewText(r.body);
        var title = decodeReviewText(r.title);
        // a title that just echoes the product name carries no sentiment — drop it
        var showTitle = title && title.trim() && title.trim().toLowerCase() !== (p.title || '').trim().toLowerCase();
        var when = formatReviewDate(r.publishedAt);
        return '<a class="ai-review-card" href="' + escapeHtml(p.url || '#') + '"' +
          ' data-ai-result data-ai-item="' + escapeHtml(p.handle || '') + '" data-ai-kind="review" data-ai-surface="' + escapeHtml(surface || '') + '">' +
          img +
          '<div class="ai-review-card__content">' +
          '<div class="ai-review-card__top">' +
          '<span class="ai-review-card__stars" aria-label="' + rating + ' out of 5 stars">' + stars + '</span>' +
          (when ? '<span class="ai-review-card__date">' + escapeHtml(when) + '</span>' : '') +
          '</div>' +
          (showTitle ? '<div class="ai-review-card__headline">' + escapeHtml(title) + '</div>' : '') +
          '<p class="ai-review-card__body">&ldquo;' + escapeHtml(body) + '&rdquo;</p>' +
          '<div class="ai-review-card__meta">' + escapeHtml(r.reviewer || '') +
          (r.verified ? ' · <span class="ai-review-card__verified">✓ Verified buyer</span>' : '') +
          (p.title ? ' · on ' + escapeHtml(p.title) : '') + '</div>' +
          '</div></a>';
      }).join('') + '</div>';
  }

  // Zero-result recovery for /search: did-you-mean → review-led recommendations
  // → nearest-product suggestions (native cards) → ONE deduped chip row
  // (recent + trending). Async — resolves to the final HTML string.
  function renderDiscovery(query, popular, recent, items, reviews, didYouMean) {
    var cardsPromise = (items && items.length)
      ? renderProductCards(items, 'search')
      : Promise.resolve('');
    return cardsPromise.then(function (cards) {
      var html = '<div class="ai-discovery">';
      if (query) {
        html += '<p class="ai-discovery__lead">No exact match for &ldquo;' + escapeHtml(query) + '&rdquo;.' +
          (didYouMean
            ? ' Did you mean <a class="ai-didyoumean__link" href="/search?q=' + encodeURIComponent(didYouMean) + '"><strong>' + escapeHtml(didYouMean) + '</strong></a>?'
            : '') + '</p>';
      }
      if (reviews && reviews.length) {
        html += '<div class="ai-discovery__group">' + renderReviews(reviews, 'search', false) + '</div>';
      }
      if (cards) {
        html += '<div class="ai-discovery__group"><h3 class="ai-discovery__title">Shoppers looking for this often choose</h3>' +
          '<ul class="products collection row small-up-2 medium-up-3 ai-search-grid" role="list">' + cards + '</ul></div>';
      }
      var merged = [];
      var seen = {};
      (recent || []).concat(popular || []).forEach(function (q2) {
        var k = String(q2 || '').toLowerCase();
        if (!k || seen[k] || (query && k === String(query).toLowerCase())) return;
        seen[k] = true;
        merged.push(q2);
      });
      merged = merged.slice(0, 8);
      if (merged.length) {
        html += '<div class="ai-discovery__group"><h3 class="ai-discovery__title">Try a different search</h3>' +
          '<div class="ai-discovery__chips">' +
          merged.map(function (q2) {
            return '<a class="ai-discovery__chip" href="/search?q=' + encodeURIComponent(q2) + '">' + escapeHtml(q2) + '</a>';
          }).join('') + '</div></div>';
      }
      html += '</div>';
      return html;
    });
  }

  /*
   * Add-to-cart from AI result cards. Theme idiom: AJAX add with section
   * rendering (cart-drawer + cart-bubble), swap the bubble count + drawer
   * content, then open the cart drawer like the theme's own add-to-cart.js.
   * Every step is guarded — worst case the button still shows "Added ✓".
   */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-ai-atc]') : null;
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var label = btn.querySelector('span') || btn;
    var orig = label.textContent;
    try {
      if (window.AISearchAnalytics) {
        window.AISearchAnalytics.logClick(btn.getAttribute('data-ai-handle'), 'product',
          Number(btn.getAttribute('data-ai-btn-position')) || null, btn.getAttribute('data-ai-btn-surface') || '');
      }
    } catch (err) {}
    btn.disabled = true;
    label.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: Number(btn.getAttribute('data-ai-atc')), quantity: 1 }),
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function () {
        label.textContent = 'Added ✓';
        fetch('/?sections=cart-drawer,cart-bubble')
          .then(function (r) { return r.json(); })
          .then(function (j) {
            try {
              var toggle = document.getElementById('cart-drawer-toggle');
              if (toggle && j && j['cart-bubble']) {
                var d1 = document.createElement('div');
                d1.innerHTML = j['cart-bubble'];
                var bubble = d1.querySelector('.thb-item-count');
                var cur = toggle.querySelector('.thb-item-count');
                if (bubble && cur) cur.outerHTML = bubble.outerHTML;
              }
              var drawerEl = document.getElementById('Cart-Drawer');
              if (drawerEl && j && j['cart-drawer']) {
                var d2 = document.createElement('div');
                d2.innerHTML = j['cart-drawer'];
                var inner = d2.querySelector('.cart-drawer');
                var curInner = drawerEl.querySelector('.cart-drawer');
                if (inner && curInner) curInner.innerHTML = inner.innerHTML;
                if (typeof CartDrawer !== 'undefined') new CartDrawer();
                document.body.classList.add('open-cc');
                document.body.classList.add('open-cart');
                drawerEl.classList.add('active');
                if (typeof dispatchCustomEvent !== 'undefined') dispatchCustomEvent('cart-drawer:open');
              }
            } catch (err) {}
          })
          .catch(function () {});
        setTimeout(function () { label.textContent = orig; btn.disabled = false; }, 2000);
      })
      .catch(function () { label.textContent = orig; btn.disabled = false; });
  }, true);

  window.AISearchClient = {
    ready: ready,
    query: query,
    trending: trending,
    reviewsHighlights: reviewsHighlights,
    escapeHtml: escapeHtml,
    formatPrice: formatPrice,
    renderStars: renderStars,
    renderCard: renderCard,
    renderProductCards: renderProductCards,
    renderArticlesSection: renderArticlesSection,
    applyResults: applyResults,
    renderGridContainer: renderGridContainer,
    syncCounts: syncCounts,
    renderDrawer: renderDrawer,
    renderReviews: renderReviews,
    renderDiscovery: renderDiscovery,
  };
})();
